import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { fromEvent, race, Subject, timer, zip } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';
import {
  AppConfig,
  BackendResponse,
  DataManagementService,
  DeviceService,
  DialogService,
  HttpService,
  ScreenSize,
  ToastrService,
  TranslateService,
} from 'vas-mobile-api';

import { LoadingSlotPopupComponent } from '../loading-slot-popup/loading-slot-popup.component';
import { Column, Option } from '../models/chips-and-fields';
import { Delivery, DeliveryPos } from '../models/delivery';
import { SlotDetail } from '../models/loadingSlot';
import { PictureViewerComponent } from '../picture-viewer/picture-viewer.component';
import { DataExchangeService } from '../services/dataexchange.service';
import { PrintService } from '../services/print.service';

@Component({
  selector: 'app-delivery-detail',
  templateUrl: './delivery-detail.component.html',
  styleUrls: ['./delivery-detail.component.css']
})
export class DeliveryDetailComponent implements OnInit, AfterViewInit, OnDestroy {

  // view configs
  public fields: Column[];
  public positionFields: Column[];

  public delivery: Partial<Delivery> = {};
  public loadingSlot: Partial<SlotDetail> = {};

  @ViewChild('scrollContainer', { read: ElementRef, static: true }) private container: ElementRef;

  public showIndicatorLeft: boolean;
  public showIndicatorRight: boolean;
  private ngUnsubscribe: Subject<any> = new Subject();
  private isMobile = false;
  public locale: string;
  public userType = null;

  constructor(
    private httpService: HttpService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private des: DataExchangeService,
    private deviceService: DeviceService,
    private router: Router,
    private dialogService: DialogService,
    private toastService: ToastrService,
    private translateService: TranslateService,
    private appConfig: AppConfig,
    private dataMsgSrv: DataManagementService,
    private printService: PrintService) { }

  ngOnInit() {
    this.locale = this.translateService.getLanguage();

    this.dataMsgSrv.getUserType().then((userType) => {
      this.userType = userType;
      let pathConfig = '';
      if (this.userType === 'SUPP') {
        pathConfig = 'assets/config/deliveryDetailConfig_inbound.json';
      } else {
        pathConfig = 'assets/config/deliveryDetailConfig.json';
      }
      this.httpService.getLocalResource(pathConfig).subscribe(
        (config: { detailColumns: Column[], positionColumns: Column[] }) => {
          this.fields = config.detailColumns;
          this.positionFields = config.positionColumns;

          const fieldKeys = this.fields.map((v) => v.key);
          const positionFieldkeys = this.positionFields.map((v) => v.key);
          positionFieldkeys.push('LIEFERPOS_NR');
          positionFieldkeys.push('ARTIKEL_NR');

          // route Params -> Delivery -> Positions -> Result is Delivery with Positions
          this.route.paramMap.pipe(switchMap((params: ParamMap) => {
            return this.httpService.get<BackendResponse<Delivery>>({
              pathId: 'showFilteredDelivery', urlParams: {
                liefer_nr: params.get('lieferNr'),
                includedFields: fieldKeys
              }
            }).pipe(switchMap((result) => {
              const deliveryPosObservable = this.httpService.get<BackendResponse<DeliveryPos[]>>({
                pathId: 'getPositionsByDeliveryNr'
                , urlParams: {
                  liefer_nr: result.data.LIEFER_NR,
                  fields: positionFieldkeys
                }
              });
              const slotDetailObservable = this.httpService.get<BackendResponse<SlotDetail>>({
                pathId: 'getSlotForDelivery',
                urlParams: {
                  liefer_nr: result.data.LIEFER_NR,
                  // includedFields: slotFieldKeys
                }
              });
              return zip(deliveryPosObservable, slotDetailObservable).pipe(map((zipResult) => {
                result.data.positions = zipResult[0].data;
                result.data.loadingSlot = zipResult[1].data;
                return result.data;
              }));
            }));
          })).subscribe((delivery) => {
            this.delivery = delivery;
          });

        });

    }).catch((error) => {
      this.userType = null;
    });


    this.des.back$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.goBack();
    });

    this.deviceService.size.pipe(takeUntil(this.ngUnsubscribe)).subscribe((size) => {
      this.isMobile = size < ScreenSize.lg;
    });

  }

  ngAfterViewInit(): void {

    // Wait for ngFor to finish. This is normally the case in ngAfterViewInit, but here the data is delayed.
    const observer = new MutationObserver((mutations) => {
      if (this.container.nativeElement.offsetWidth < this.container.nativeElement.scrollWidth && this.delivery.positions.length > 1) {
        this.showIndicatorRight = true;
      }
    });

    observer.observe(this.container.nativeElement, { childList: true });

    fromEvent(this.container.nativeElement, 'scroll').pipe(
      takeUntil(this.ngUnsubscribe),
      filter(() => this.delivery.positions.length > 1))
      .subscribe((event: any) => {
        this.adjustIndicators();
      });

    fromEvent(window, 'resize').pipe(
      takeUntil(this.ngUnsubscribe),
      filter(() => this.delivery.positions.length > 1))
      .subscribe((event: any) => {
        this.adjustIndicators();
      });

    this.des.changeTitelBarName('LBLPANEL_DETAILSCREEN');

  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public getOptionValue(options: Option[], key: string) {
    if (key) {
      const option = options.find((o) => o.key === key);
      return option ? option.value : '??? (' + key + ')';
    }
    return '';
  }

  public printDeliveryNote() {
    if (this.userType === 'SUPP') {
      this.print(this.appConfig.getValue('reportSupDN'), true);
    } else {
      this.print(this.appConfig.getValue('reportIdDN'), true);
    }
  }

  public printLoadingNote() {

    if (this.userType === 'SUPP') {
      this.print(this.appConfig.getValue('reportSupLN'), false);
    } else {
      this.print(this.appConfig.getValue('reportIdLN'), false);
    }

  }

  private print(reportId: string, fromArchive: boolean) {
    this.des.showLoadingIndicator(true);
    let formCode = '';
    if (this.userType === 'SUPP') {
      formCode = this.appConfig.getValue('dnoFormularCode_Sup');
    } else {
      formCode = this.appConfig.getValue('dnoFormularCode');
    }
    if (fromArchive) {
      this.printService.printDNOFromArchive(
        reportId, this.delivery.LIEFER_NR, formCode, this.isMobile).subscribe(() => {
          this.des.showLoadingIndicator(false);
        });
    } else {
      this.printService.printDNO(reportId, this.delivery.LIEFER_NR, this.isMobile).subscribe(() => {
        this.des.showLoadingIndicator(false);
      });
    }
  }

  public openPicturesModal() {

    if (this.delivery.LIEFER_NR) {
      this.dialog.open(PictureViewerComponent, {
        data: {
          lieferNr: this.delivery.LIEFER_NR
        },
        panelClass: 'picturePanel'
      });
    }

  }

  public openSlotModal() {
    if (this.delivery.LIEFER_NR) {
      const dialogRef = this.dialog.open(LoadingSlotPopupComponent, {
        data: {
          delivery: this.delivery
        },
        panelClass: 'slotSelectionPanel'
      });
      dialogRef.afterClosed().subscribe(() => {
        this.ngOnDestroy();
        this.ngOnInit();
      });
    }
  }

  public scrollRight() {
    if (this.container.nativeElement.children.length > 0) {
      this.scroll(this.container.nativeElement, this.container.nativeElement.children[0].offsetWidth + 10, 500); // +10 for margin
    }
  }

  public scrollLeft() {
    if (this.container.nativeElement.children.length > 0) {
      this.scroll(this.container.nativeElement, -this.container.nativeElement.children[0].offsetWidth - 10, 500); // -10 for margin
    }
  }

  private adjustIndicators() {
    if (this.container.nativeElement.scrollLeft > 0) {
      this.showIndicatorLeft = true;
    } else {
      this.showIndicatorLeft = false;
    }
    if (this.container.nativeElement.scrollWidth - this.container.nativeElement.scrollLeft > this.container.nativeElement.offsetWidth) {
      this.showIndicatorRight = true;
    } else {
      this.showIndicatorRight = false;
    }
  }

  private scroll(el: any, distance: number, duration: number) {
    const startTime = Date.now();
    const endTime = startTime + duration;
    let previousLeft = el.scrollLeft;
    const startLeft = el.scrollLeft;
    const finished: Subject<boolean> = new Subject();
    const takeUntil$ = race(this.ngUnsubscribe, finished);
    timer(0, 5).pipe(takeUntil(takeUntil$)).subscribe(() => {
      if (el.scrollLeft !== previousLeft) {
        // interrupted
        finished.next();
        finished.complete();
        return;
      }
      const now = Date.now();
      const point = this.smooth_step(startTime, endTime, now);
      const frameLeft = Math.round(startLeft + (distance * point));
      el.scrollLeft = frameLeft;

      if (now >= endTime) {
        // finished
        finished.next();
        finished.complete();
        return;
      }

      if (el.scrollLeft === previousLeft && el.scrollLeft !== frameLeft) {
        // limit reached
        finished.next();
        finished.complete();
        return;
      }

      previousLeft = el.scrollLeft;
    });
  }

  private smooth_step(start: number, end: number, point: number) {
    if (point <= start) { return 0; }
    if (point >= end) { return 1; }
    const x = (point - start) / (end - start);
    return x * x * (3 - 2 * x);
  }

  public deleteDelivery() {
    this.dialogService.openConfirmCancelDialog(
      this.translateService.translateFirstLetterUpperCase('LABEL_CONFIRMATION'),
      this.translateService.translate('VASCENTER:DELETE_DELIVERY'),
      this.translateService.translate('BUTTON_YES'),
      this.translateService.translate('BUTTON_NO'))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.des.showLoadingIndicator(true);
          this.httpService.delete<BackendResponse<string>>(
            { pathId: 'deleteIDispoYCCDelivery', urlParams: { liefer_nr: this.delivery.LIEFER_NR } }
          ).subscribe((response) => {
            this.des.showLoadingIndicator(false);
            this.toastService.success(response.data);
            this.goBack();
          }, () => {
            this.des.showLoadingIndicator(false);
          });
        }
      });
  }

  private goBack() {
    const status = this.delivery.LIEFERSCHEINSTATUS;
    if (status && status !== 'L') {
      this.router.navigate(['statusList']);
    } else {
      this.router.navigate(['deliveriesList']);
    }
  }

}
